import { z } from 'zod';

// Riga ENTRATA (input al documento PSC = grezzo prelevato dal magazzino
// principale = "Uscita" del magazzino principale nella terminologia Drive/UI).
// Al close genera StockMovement.OUT che decrementa LotPosition.residualQty.
export const pscLineEntrataSchema = z.object({
  lotId: z.string().uuid('Lotto richiesto'),
  productId: z.string().uuid().nullable().optional(),
  qty: z.coerce.number().positive('Peso > 0'),
  note: z.string().max(500).nullable().optional(),
});
export type PscLineEntrata = z.infer<typeof pscLineEntrataSchema>;

// Riga USCITA (output dal documento PSC = lotto lavorato rilasciato al
// magazzino produzione = "Entrata" del magazzino produzione nella terminologia
// Drive/UI). Al close genera StockMovement.IN che incrementa LotPosition.
export const pscLineUscitaSchema = z.object({
  lotId: z.string().uuid('Lotto richiesto'),
  productId: z.string().uuid().nullable().optional(),
  qty: z.coerce.number().positive('Peso > 0'),
  pesoIniziale: z.coerce.number().nonnegative().nullable().optional(),
  pesoFinale: z.coerce.number().nonnegative().nullable().optional(),
  hasMold: z.boolean().default(false),
  isDry: z.boolean().default(false),
  isWet: z.boolean().default(false),
  note: z.string().max(500).nullable().optional(),
});
export type PscLineUscita = z.infer<typeof pscLineUscitaSchema>;

// Create: documento in DRAFT con header + opzionali entrate/uscite
export const pscCreateRequestSchema = z.object({
  draftName: z
    .string()
    .min(1, 'Nome lavorazione obbligatorio')
    .max(100, 'Nome max 100 caratteri'),
  responsable: z.string().max(100).nullable().optional(),
  workDate: z.coerce.date(),
  notes: z.string().max(2000).nullable().optional(),
  entrate: z.array(pscLineEntrataSchema).default([]),
  uscite: z.array(pscLineUscitaSchema).default([]),
});
export type PscCreateRequest = z.infer<typeof pscCreateRequestSchema>;

// Update: tutti i campi opzionali, valido solo se status = DRAFT
export const pscUpdateRequestSchema = pscCreateRequestSchema.partial();
export type PscUpdateRequest = z.infer<typeof pscUpdateRequestSchema>;

// Close: trigger transition DRAFT -> CLOSED (no body)
export const pscCloseRequestSchema = z.object({}).strict();
export type PscCloseRequest = z.infer<typeof pscCloseRequestSchema>;

// Response shapes
const documentStatusSchema = z.enum(['DRAFT', 'CLOSED', 'SUPERSEDED']);

export const pscLineResponseSchema = z.object({
  id: z.string(),
  lineNumber: z.number().int().positive(),
  direction: z.enum(['IN', 'OUT']),
  lotId: z.string().nullable(),
  productId: z.string().nullable(),
  qty: z.string(), // serializzato Decimal -> stringa per evitare precision loss in JSON
  pesoIniziale: z.string().nullable(),
  pesoFinale: z.string().nullable(),
  hasMold: z.boolean(),
  isDry: z.boolean(),
  isWet: z.boolean(),
  note: z.string().nullable(),
  // Hydration helpers per UI
  lotCode: z.string().nullable(),
  productCode: z.string().nullable(),
  productName: z.string().nullable(),
});
export type PscLineResponse = z.infer<typeof pscLineResponseSchema>;

export const pscResponseSchema = z.object({
  id: z.string(),
  type: z.literal('PSC'),
  code: z.string().nullable(), // numero progressivo (CLOSED) o null (DRAFT)
  draftName: z.string().nullable(),
  workDate: z.string(), // ISO
  periodId: z.string(),
  period: z
    .object({
      year: z.number().int(),
      month: z.number().int(),
      status: z.enum(['OPEN', 'CLOSED']),
    })
    .optional(),
  status: documentStatusSchema,
  locked: z.boolean(),
  responsable: z.string().nullable(),
  notes: z.string().nullable(),
  createdById: z.string(),
  closedById: z.string().nullable(),
  createdAt: z.string(),
  closedAt: z.string().nullable(),
  updatedAt: z.string(),
  entrate: z.array(pscLineResponseSchema),
  uscite: z.array(pscLineResponseSchema),
  // Computed totals (server-side)
  entrataTotG: z.string(),
  uscitaTotG: z.string(),
  sfridoG: z.string(),
  sfridoPct: z.number(),
});
export type PscResponse = z.infer<typeof pscResponseSchema>;

export const pscListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  status: documentStatusSchema.optional(),
  periodId: z.string().uuid().optional(),
  search: z.string().trim().min(1).max(100).optional(),
  workDateFrom: z.coerce.date().optional(),
  workDateTo: z.coerce.date().optional(),
  format: z.enum(['json', 'xlsx']).default('json'),
});
export type PscListQuery = z.infer<typeof pscListQuerySchema>;

export const pscListItemSchema = z.object({
  id: z.string(),
  type: z.literal('PSC'),
  code: z.string().nullable(),
  draftName: z.string().nullable(),
  workDate: z.string(),
  status: documentStatusSchema,
  responsable: z.string().nullable(),
  closedAt: z.string().nullable(),
  entrataTotG: z.string(),
  uscitaTotG: z.string(),
  sfridoG: z.string(),
  sfridoPct: z.number(),
});
export type PscListItem = z.infer<typeof pscListItemSchema>;

export const pscListResponseSchema = z.object({
  items: z.array(pscListItemSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});
export type PscListResponse = z.infer<typeof pscListResponseSchema>;
